import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Seo, { Breadcrumbs } from "@/components/Seo";
import { StarRating } from "@/components/StarRating";
import { Users, Fuel, Shield, MessageCircle, ArrowRight, Check } from "lucide-react";

function fmtDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function eachDayInclusive(start, end) {
  const days = [];
  const d = new Date(start);
  while (d <= end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
  return days;
}

export default function VehicleDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [range, setRange] = useState(undefined);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "", author_name: "" });
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgForm, setMsgForm] = useState({ subject: "", body: "", guest_name: "", guest_email: "" });

  useEffect(() => {
    api.get(`/vehicles/${slug}`).then((r) => setV(r.data)).catch(() => setV(false));
  }, [slug]);

  const disabledDates = useMemo(() => {
    if (!v?.unavailable) return [];
    const dates = [];
    for (const range of v.unavailable) {
      const s = new Date(range.start + "T00:00:00");
      const e = new Date(range.end + "T00:00:00");
      dates.push(...eachDayInclusive(s, e));
    }
    return dates;
  }, [v]);

  const days = range?.from && range?.to ? Math.max(1, Math.round((range.to - range.from) / (1000 * 60 * 60 * 24)) + 1) : 0;
  const subtotal = v && days ? days * v.price_per_day : 0;
  // Pick the highest applicable tier from the vehicle's own deal_tiers list.
  const dealTiers = (v?.deal_tiers || []).slice().sort((a, b) => (a.min_days || 0) - (b.min_days || 0));
  const activeTier = dealTiers.reduce((best, t) => (days >= (t.min_days || 0) ? t : best), null);
  const tier = { pct: activeTier?.pct || 0, label: activeTier?.label || null };
  const tierSavings = subtotal * tier.pct;
  const discountedSubtotal = subtotal - tierSavings;
  const taxes = subtotal ? +(discountedSubtotal * 0.0875).toFixed(2) : 0;
  const total = subtotal ? +(discountedSubtotal + taxes).toFixed(2) : 0;
  const effectivePerDay = days ? discountedSubtotal / days : (v?.price_per_day || 0);
  const nextTier = dealTiers.find((t) => days < (t.min_days || 0));

  if (v === false) return <div className="container-bl py-20 text-center">Vehicle not found. <Link to="/vehicles" className="link-blue">Browse all</Link></div>;
  if (!v) return <div className="container-bl py-20">Loading…</div>;

  const onBook = () => {
    if (!range?.from || !range?.to) {
      toast.error("Please pick start and end dates");
      return;
    }
    if (days < (v.min_days || 1)) {
      toast.error(`Minimum rental is ${v.min_days} days`);
      return;
    }
    const params = new URLSearchParams({ slug: v.slug, start: fmtDate(range.from), end: fmtDate(range.to) });
    nav(`/booking?${params.toString()}`);
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { vehicle_id: v.vehicle_id, rating: reviewForm.rating, comment: reviewForm.comment, author_name: reviewForm.author_name });
      toast.success("Thanks for your review!");
      setShowReview(false);
      const { data } = await api.get(`/vehicles/${slug}`);
      setV(data);
      setReviewForm({ rating: 5, comment: "", author_name: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitMessage = async () => {
    try {
      await api.post("/messages", {
        subject: msgForm.subject || `Question about ${v.name}`,
        body: msgForm.body,
        guest_email: msgForm.guest_email || undefined,
        guest_name: msgForm.guest_name || undefined,
      });
      toast.success("Message sent — support will reply by email.");
      setMsgOpen(false);
      setMsgForm({ subject: "", body: "", guest_name: "", guest_email: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": v.name,
    "image": v.images,
    "description": v.description,
    "brand": {"@type": "Brand", "name": "B&L Rentals"},
    "aggregateRating": {"@type": "AggregateRating", "ratingValue": String(v.rating || 5), "reviewCount": String(v.review_count || 1)},
    "offers": {"@type": "Offer", "priceCurrency": "USD", "price": v.price_per_day, "availability": "https://schema.org/InStock", "url": typeof window !== "undefined" ? window.location.href : ""},
  };

  return (
    <div className="container-bl py-8" data-testid={`page-vehicle-${v.slug}`}>
      <Seo
        title={`${v.type} Rental in Oxnard · ${v.name} | B&L Rentals`}
        description={`Rent a ${v.type.toLowerCase()} in Oxnard: ${v.name}. ${v.short_desc}`}
        canonical={typeof window !== "undefined" ? window.location.origin + `/vehicles/${v.slug}` : undefined}
        image={v.hero_image}
        type="product"
        schema={schema}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Vehicles", to: "/vehicles" }, { label: v.name }]} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT: Gallery + info */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden bg-gray-100 aspect-[16/10]">
            <img src={v.images?.[selectedImage] || v.hero_image} alt={v.name} className="w-full h-full object-cover" />
          </div>
          <div className="mt-3 grid grid-cols-4 md:grid-cols-5 gap-2">
            {v.images?.slice(0, 10).map((img, i) => (
              <button
                key={i}
                onClick={() => setSelectedImage(i)}
                className={`aspect-[4/3] overflow-hidden rounded-lg border-2 transition ${selectedImage === i ? "border-[#E2AB20]" : "border-transparent hover:border-gray-200"}`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={img} alt={`${v.name} view ${i+1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-[#E2AB20] font-semibold">{v.type} · {v.year}</div>
            <StarRating value={v.rating || 5} testId="vehicle-rating" />
            <span className="text-sm text-[#AEB9C9]">({v.review_count} reviews)</span>
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-heading font-bold text-white" data-testid="vehicle-name">{v.name}</h1>
          <p className="mt-4 text-[#AEB9C9] text-base leading-relaxed">{v.description}</p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
            {v.amenities?.map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-[#E5EAF2]">
                <Check size={16} className="text-[#E2AB20]" /> {a}
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-heading font-bold text-white">Features</h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {v.features?.map((f) => (
                <div key={f} className="card-bl p-4 text-sm text-[#E5EAF2]">{f}</div>
              ))}
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-10 border-t border-gray-200 pt-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-heading font-bold text-white">Reviews</h2>
              <Dialog open={showReview} onOpenChange={setShowReview}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="write-review-btn">Write a review</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Share your experience</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Your name</Label>
                      <Input value={reviewForm.author_name} onChange={(e) => setReviewForm({...reviewForm, author_name: e.target.value})} placeholder="Optional" data-testid="review-name" />
                    </div>
                    <div>
                      <Label>Rating</Label>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5].map((n) => (
                          <button key={n} onClick={() => setReviewForm({...reviewForm, rating: n})} data-testid={`review-star-${n}`}>
                            <StarRating value={n <= reviewForm.rating ? n : 0} size={22} showValue={false} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Comment</Label>
                      <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})} placeholder="How was your trip?" data-testid="review-comment" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={submitReview} className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="review-submit">Submit review</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="mt-5 space-y-4">
              {v.reviews?.length === 0 && <div className="text-sm text-[#AEB9C9]">No reviews yet. Be the first!</div>}
              {v.reviews?.map((r) => (
                <div key={r.review_id} className="card-bl p-5" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-white">{r.author_name}</div>
                    <StarRating value={r.rating} showValue={false} size={14} />
                  </div>
                  <p className="mt-2 text-sm text-[#AEB9C9]">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Booking widget */}
        <div className="lg:col-span-1">
          <div className="card-bl p-6 sticky top-24" data-testid="booking-widget">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="text-3xl font-bold text-white">${v.price_per_day}</span>
                <span className="text-sm text-[#AEB9C9] ml-1">/day</span>
              </div>
              <StarRating value={v.rating || 5} size={13} />
            </div>
            <div className="mt-1 text-xs text-[#AEB9C9]">Min {v.min_days} day{v.min_days > 1 ? "s" : ""} · {v.capacity} seats</div>

            <div className="mt-4 rounded-lg border border-[#E2AB20]/20 bg-[#E2AB20]/5 p-3 text-xs text-[#E5EAF2]" data-testid="tier-summary">
              <div className="font-heading font-semibold text-[#E2AB20] uppercase tracking-widest text-[10px]">Auto-applied deals</div>
              <ul className="mt-1.5 space-y-0.5">
                {dealTiers.length === 0 && <li>· No multi-day discounts on this vehicle.</li>}
                {dealTiers.map((t) => (
                  <li key={t.min_days}>· <b>{t.min_days}+ days</b> — save {Math.round((t.pct || 0) * 100)}%</li>
                ))}
              </ul>
            </div>

            <div className="mt-5 border rounded-lg">
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={1}
                disabled={[{ before: new Date() }, ...disabledDates]}
                data-testid="booking-calendar"
              />
            </div>

            {days > 0 && (
              <div className="mt-5 border-t border-gray-200 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[#E5EAF2]"><span>${v.price_per_day} × {days} day{days > 1 ? "s" : ""}</span><span>${subtotal.toFixed(2)}</span></div>
                {tier.label && (
                  <div className="flex justify-between text-green-700 font-medium" data-testid="tier-discount-row">
                    <span>{tier.label} ({Math.round(tier.pct * 100)}% off)</span>
                    <span>-${tierSavings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#E5EAF2]"><span>Taxes & fees (8.75%)</span><span>${taxes.toFixed(2)}</span></div>
                <div className="flex justify-between font-semibold text-white pt-2 border-t border-gray-100"><span>Total (before referral)</span><span data-testid="booking-total">${total.toFixed(2)}</span></div>
                {tierSavings > 0 && (
                  <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-800" data-testid="savings-callout">
                    <div className="font-semibold">You save ${tierSavings.toFixed(2)} with the {tier.label}!</div>
                    <div className="mt-0.5">Effective rate: <b>${effectivePerDay.toFixed(2)}/day</b> · normally ${v.price_per_day}/day</div>
                  </div>
                )}
                {!tier.label && dealTiers.length > 0 && nextTier && (
                  <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900" data-testid="deal-hint">
                    Extend to <b>{nextTier.min_days}+ days</b> to unlock {Math.round((nextTier.pct || 0) * 100)}% off.
                  </div>
                )}
              </div>
            )}

            <Button className="mt-5 w-full bg-[#E2AB20] hover:bg-[#D89B08] text-white h-12 text-base" onClick={onBook} data-testid="continue-to-booking">
              Continue to booking <ArrowRight size={18} className="ml-2" />
            </Button>

            <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="mt-3 w-full" data-testid="message-support-btn"><MessageCircle size={16} className="mr-2" /> Message Support</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ask us about the {v.name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Your name</Label>
                      <Input value={msgForm.guest_name} onChange={(e) => setMsgForm({...msgForm, guest_name: e.target.value})} data-testid="msg-name" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={msgForm.guest_email} onChange={(e) => setMsgForm({...msgForm, guest_email: e.target.value})} data-testid="msg-email" />
                    </div>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={msgForm.subject} onChange={(e) => setMsgForm({...msgForm, subject: e.target.value})} data-testid="msg-subject" />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea value={msgForm.body} onChange={(e) => setMsgForm({...msgForm, body: e.target.value})} data-testid="msg-body" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitMessage} className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="msg-submit">Send message</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="mt-6 space-y-2 text-xs text-[#AEB9C9]">
              <div className="flex items-center gap-2"><Check size={12} className="text-[#E2AB20]" /> <span><b className="text-white">Unlimited miles</b> — no mileage restrictions.</span></div>
              <div className="flex items-center gap-2"><Fuel size={12} className="text-[#E2AB20]" /> {v.type?.toLowerCase().includes("electric") ? "Return with same battery charge or pay the charging amount plus a $20 fee." : "Return with same fuel level or pay the fuel amount plus a $20 fee."}</div>
              <div className="flex items-center gap-2"><Shield size={12} className="text-[#E2AB20]" /> Insurance verification required before pickup.</div>
              <div className="flex items-center gap-2"><Users size={12} className="text-[#E2AB20]" /> Free cancellation up to 48 hours.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
